/**
 * Query-string serialization exactly as axios 0.27 wrote it.
 *
 * axios 1.x changed the default: a nested object in `params` became bracket notation
 * (`filter[sector]=Tech`) where 0.27 sent it as JSON (`filter={"sector":"Tech"}`). The backend
 * was written against 0.27, so every instance keeps the old format rather than each call site
 * being audited for object-valued params. Arrays stay `key[]=a&key[]=b`, Dates are ISO strings,
 * null and undefined values are dropped.
 */
const encode = (val: string): string =>
    encodeURIComponent(val).replace(/%3A/gi, ":").replace(/%24/g, "$").replace(/%2C/gi, ",").replace(/%20/g, "+").replace(/%5B/gi, "[").replace(/%5D/gi, "]");

const stringify = (v: unknown): string => {
    if (v instanceof Date) return v.toISOString();
    if (v !== null && typeof v === "object") return JSON.stringify(v);
    return String(v);
};

export const legacyParamsSerializer = (params: unknown): string => {
    if (!params || typeof params !== "object") return "";
    if (typeof URLSearchParams !== "undefined" && params instanceof URLSearchParams) return params.toString();
    const parts: string[] = [];
    Object.keys(params).forEach((key) => {
        const val = (params as Record<string, unknown>)[key];
        if (val === null || val === undefined) return;
        const name = Array.isArray(val) ? `${key}[]` : key;
        (Array.isArray(val) ? val : [val]).forEach((v) => parts.push(`${encode(name)}=${encode(stringify(v))}`));
    });
    return parts.join("&");
};

/**
 * `withXSRFToken` for an instance whose API lives at `base`.
 *
 * 0.27 put the CSRF cookie into the header of every `withCredentials` request, to any host;
 * 1.x (the CVE-2023-45857 fix) sends it cross-origin only when told to. The ORTEX and Vertex
 * APIs are on other origins than the page, so this tells it to, for requests to that API only.
 * `undefined` leaves axios's own same-origin rule in charge for everything else.
 */
export const xsrfForApi =
    (base: string | undefined) =>
    (config: { url?: string; baseURL?: string; withCredentials?: boolean }): true | undefined => {
        if (!base || !config.withCredentials) return undefined;
        try {
            return new URL(config.url ?? "", config.baseURL ?? base).origin === new URL(base).origin ? true : undefined;
        } catch {
            return undefined;
        }
    };
